import React, { useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Collapse,
  useTheme,
  Grid,
  TextField,
} from "@mui/material";
import BgrExteriorForm from "./services/BgrExteriorForm";
import BgrManualForm from "./services/BgrManualForm";
import BgrPanoForm from "./services/BgrPanoForm";
import CropForm from "./services/CropForm";
import FeaturedForm from "./services/FeaturedForm";
import ClickInsForm from "./services/ClickInsForm";
import DewarpingForm from "./services/VideoDewarpingForm";

const SERVICE_COMPONENTS: Record<string, React.FC<any>> = {
  bgrExterior: BgrExteriorForm,
  bgrManual: BgrManualForm,
  bgrPano: BgrPanoForm,
  crop: CropForm,
  featured: FeaturedForm,
  clickIns: ClickInsForm,
  videoDewarping: DewarpingForm,
};

// Consistent flow order (this determines flowControl order)
const SERVICE_ORDER = [
  "bgrExterior",
  "bgrManual",
  "bgrPano",
  "crop",
  "featured",
  "clickIns",
  "videoDewarping",
];

const SERVICE_FLAGS: Record<string, string[]> = {
  bgrExterior: ["assetProcessing_bgrExterior_serviceFlag"],
  bgrManual: ["assetProcessing_bgrManual_serviceFlag"],
  bgrPano: ["assetProcessing_bgrPano_serviceFlag"],
  crop: [
    "assetProcessing_masking_serviceFlag",
    "assetProcessing_crop_serviceFlag",
  ],
  featured: [
    "assetProcessing_featured_serviceFlag",
    "assetProcessing_masking_serviceFlag",
  ],
  clickIns: ["assetProcessing_clickIns_serviceFlag"],
  videoDewarping: ["assetProcessing_videoDewarping_serviceFlag"],
};

interface AssetProcessingFormProps {
  config: any;
  handleChange: (path: string, value: any) => void;
}

const AssetProcessingForm: React.FC<AssetProcessingFormProps> = ({
  config,
  handleChange,
}) => {
  const theme = useTheme();
  const red = "#C62828"; // UMS Red
  const cachedConfigsRef = useRef<Record<string, any>>({});
  console.log(config)

  const enabledServices = useMemo(() => {
    return Object.keys(SERVICE_COMPONENTS).filter((s) => {
      const key = `assetProcessing_${s}_serviceConfig`;
      return config[key] !== undefined;
    });
  }, [config]);

  /** 🔁 Rebuilds flow steps in the correct order and logic */
  const rebuildFlowSteps = (services: string[]): string[] => {
    const ordered = SERVICE_ORDER.filter((s) => services.includes(s));
    const steps: string[] = [];

    ordered.forEach((service) => {
      switch (service) {
        case "bgrExterior":
          steps.push("assetProcessing_bgrExterior_serviceFlag");
          break;
        case "bgrManual":
          steps.push("assetProcessing_bgrManual_serviceFlag");
          break;
        case "bgrPano":
          steps.push("assetProcessing_bgrPano_serviceFlag");
          break;
        case "crop":
          steps.push("assetProcessing_masking_serviceFlag");
          steps.push("assetProcessing_crop_serviceFlag");
          break;
        case "featured":
          // Add masking only if crop wasn't selected
          if (!services.includes("crop")) {
            steps.push("assetProcessing_masking_serviceFlag");
          }
          steps.push("assetProcessing_featured_serviceFlag");
          break;
        case "clickIns":
          steps.push("assetProcessing_clickIns_serviceFlag");
          break;
        case "videoDewarping":
          steps.push("assetProcessing_videoDewarping_serviceFlag");
          break;
      }
    });

    return steps;
  };

  /** 🔘 Enable/disable a service */
  const toggleService = (service: string, enabled: boolean) => {
    const serviceKey = `assetProcessing_${service}_serviceConfig`;
    console.log(cachedConfigsRef.current[service])

    // Update configs
    if (enabled) {
      const restored = cachedConfigsRef.current[service] || {};
      console.log("Reenabled", serviceKey, restored)
      handleChange(serviceKey, restored);
      cachedConfigsRef.current[service] = restored;
    } else {
      cachedConfigsRef.current[service] = config[serviceKey];
      handleChange(serviceKey, undefined);
    }

    // Determine new enabled list
    const updatedEnabled = enabled
      ? [...enabledServices, service]
      : enabledServices.filter((s) => s !== service);

    // ✅ Rebuild flowControl based on updated services
    const newFlow = rebuildFlowSteps(updatedEnabled);
    handleChange("flowControl_assetProcessing_steps", newFlow);
  };

  return (
    <Paper
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: "0 0 6px rgba(0,0,0,0.3)",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          fontWeight: 600,
          color: red,
          letterSpacing: 0.3,
        }}
      >
        Asset Processing Services
      </Typography>

      {/* Checkbox row */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {SERVICE_ORDER.map((service) => {
          const enabled = enabledServices.includes(service);
          return (
            <Grid item key={service}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enabled}
                    onChange={(e) => toggleService(service, e.target.checked)}
                    sx={{
                      "&.Mui-checked": { color: red },
                      "&:hover": { backgroundColor: "rgba(198,40,40,0.1)" },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 500 }}>
                    {service
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (s) => s.toUpperCase())}
                  </Typography>
                }
              />
            </Grid>
          );
        })}
      </Grid>

      {/* Expanded mini-forms */}
      {Object.entries(SERVICE_COMPONENTS).map(([service, Component]) => {
        const serviceKey = `assetProcessing_${service}_serviceConfig`;
        const enabled = enabledServices.includes(service);
        return (
          <Collapse key={service} in={enabled} unmountOnExit>
            <Box
              sx={{
                pl: 3,
                borderLeft: `2px solid ${red}`,
                mt: 1,
                ml: 0.5,
                mb: 3,
              }}
            >
              <Component
                config={config[serviceKey] || {}}
                onChange={(field: string, value: any) =>
                  handleChange(`${serviceKey}.${field}`, value)
                }
              />
            </Box>
          </Collapse>
        );
      })}

      {/* Flow Control Steps */}
      <Typography
        variant="h6"
        sx={{
          mt: 3,
          mb: 1,
          fontWeight: 600,
          color: red,
        }}
      >
        Flow Control Steps
      </Typography>
      <TextField
        fullWidth
        multiline
        minRows={3}
        value={
          Array.isArray(config.flowControl_assetProcessing_steps)
            ? config.flowControl_assetProcessing_steps.join("\n")
            : config.flowControl_assetProcessing_steps || ""
        }
        onChange={(e) =>
          handleChange(
            "flowControl_assetProcessing_steps",
            e.target.value
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean)
          )
        }
        sx={{
          fontFamily: "monospace",
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "#444" },
            "&:hover fieldset": { borderColor: red },
            "&.Mui-focused fieldset": { borderColor: red },
          },
          "& .MuiInputBase-input": { color: "#fff" },
        }}
      />
    </Paper>
  );
};

export default AssetProcessingForm;
